<script>
import { defineComponent } from "vue";
import SearchIcon from "@/icons/SearchIcon.vue";

export default defineComponent({
  name: "HeaderWrapper",
  components: { SearchIcon },
  props: {
    usedFiltersCount: {
      type: Number,
      default: () => 0,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    inputPlaceholder: {
      type: String,
      default: "Фильтры",
    },
    searchHideFilters: {
      type: Boolean,
      default: () => true,
    },
    searchSize: {
      type: String,
      default: () => "large",
    },
    showFilter: {
      type: Boolean,
      default: () => true,
    },
    showFooter: {
      type: Boolean,
      default: () => true,
    },
    showField: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      showFilters: false,
      query: "",
      debounce: null,

      wrapperWidth: 0,
      filtersLeft: 0,
      filtersRight: 0,
      resizeObserver: null,
    };
  },
  computed: {
    filtersMaxHeight() {
      let windowHeight = window.innerHeight;
      return `${windowHeight - 160}px`;
    },
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.$refs["wrapper"]);
    this.setFiltersStyles();
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.$refs["wrapper"]);
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    handleResize(entries) {
      for (let entry of entries) {
        this.wrapperWidth = entry.contentRect.width;
        this.setFiltersStyles();
      }
    },
    setFiltersStyles() {
      let windowInnerWidth = window.innerWidth;
      const MAX_FILTERS_WIDTH = 900;

      if (windowInnerWidth > MAX_FILTERS_WIDTH + 80) {
        let gapWidth = (windowInnerWidth - 900) / 2;
        this.filtersLeft = gapWidth;
        this.filtersRight = gapWidth;
      } else {
        this.filtersLeft = 40;
        this.filtersRight = 40;
      }
    },
    openFilters() {
      this.showFilters = true;
      this.$nextTick(() => {
        this.$refs["search-input-filters"].focus();
      });
    },
    hideFilters() {
      this.showFilters = false;
    },
    handleInputFocus() {
      this.openFilters();
    },
    handleEnter() {
      this.$emit("enter");
    },
    handleQueryChange() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        const trimmed = this.query.trim();
        this.$emit("search", trimmed.length === 0 ? "" : this.query);
      }, 400);
    },
    clearQuery() {
      this.query = "";
      this.$emit("search", this.query);
    },
  },
});
</script>

<template>
  <div ref="wrapper" class="wrapper">
    <div ref="header" class="header">
      <slot name="header"></slot>
    </div>

    <div ref="main" class="main" :class="searchSize" v-if="showFilter">
      <div class="search-wrapper">
        <SearchIcon class="search-icon" style="height: 20px; width: 20px" />
        <input
          ref="search-input"
          type="text"
          :placeholder="inputPlaceholder"
          v-model="query"
          @input="handleQueryChange"
          @focus="handleInputFocus"
        />
        <i
          v-if="query && query.length > 0 && clearable"
          @click="clearQuery"
          class="el-icon-close absolute top-3 m-2 text-gray-500 cursor-pointer hover:text-gray-700"
          style="font-size: 20px"
          :style="usedFiltersCount > 0 ? 'right: 170px' : 'right:10px'"
        ></i>
        <div
          @click="openFilters"
          v-if="usedFiltersCount > 0"
          class="filters-count"
        >
          {{ $t("Применено фильтров") }}: {{ usedFiltersCount }}
        </div>
      </div>
    </div>
    <div ref="footer" class="footer" v-if="showFooter">
      <slot name="footer"></slot>
    </div>
    <div class="w-full" v-if="showField">
      <slot name="field"></slot>
    </div>

    <div
      v-if="showFilters && $slots.filters"
      class="modal-overlay"
      style="background-color: rgba(0, 0, 0, 0.5); backdrop-filter: blur(3px)"
      @click="hideFilters"
    ></div>

    <div
      v-show="showFilters && $slots.filters"
      class="filters"
      :style="{
        left: filtersLeft + 'px',
        right: filtersRight + 'px',
        maxHeight: filtersMaxHeight,
      }"
    >
      <div class="search-wrapper in-filters">
        <SearchIcon class="search-icon" style="height: 20px; width: 20px" />
        <input
          ref="search-input-filters"
          type="text"
          :placeholder="inputPlaceholder"
          v-model="query"
          @input="handleQueryChange"
          @focus="handleInputFocus"
          @keyup.enter="handleEnter"
        />
      </div>

      <slot name="filters"></slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  @apply flex w-full bg-white;

  .header {
    @apply border-0 border-r border-solid border-gray-200 flex items-center justify-between flex-shrink-0 relative;
    padding-left: var(--page-padding-x);
    padding-right: var(--page-padding-x);
    min-width: 180px;
    max-width: 470px;

    .title {
      @apply font-bold truncate flex items-center;
      max-width: 500px;
      font-size: 17px;
    }
  }

  .search-wrapper {
    @apply relative;
    input {
      @apply w-full border-0 p-5 pl-12 outline-none leading-4;
      &::placeholder {
        @apply text-gray-400 text-base font-medium;
      }
    }
    svg.search-icon {
      margin: 7px 8px 8px 8px;
      @apply text-lg absolute top-3.5 left-2 text-gray-400;
    }
    .clear-icon {
      @apply text-gray-500 bg-gray-50 border border-solid border-gray-200 rounded flex items-center justify-center;
      font-size: 0.9rem;
      height: 1.65rem;
      width: 1.65rem;
      padding-top: 1px;
      cursor: pointer;
      &:hover {
        @apply border-blue-200 bg-blue-50 text-blue-500;
      }
    }

    .right-content {
      @apply bg-white absolute right-5 top-5 flex items-center justify-end;
      width: 160px;

      & .search-button {
        @apply text-gray-700 mr-2.5 cursor-pointer px-2 border border-solid border-gray-200 bg-gray-50 rounded flex items-center;
        height: 1.65rem;
        font-size: 0.75rem;
        padding-top: 1px;

        &:hover {
          @apply border-blue-200 bg-blue-50 text-blue-500;
        }
      }
    }

    .filters-count {
      @apply bg-gray-100 p-2.5 py-1 text-gray-500 text-xs inline-flex absolute right-2 top-5 border border-solid border-gray-300 rounded cursor-pointer hover:text-blue-500;
    }

    &.in-filters {
      @apply border-0 border-b border-solid border-gray-200;

      input {
        @apply w-full border-0 px-5 pb-5 pt-6 pl-12 outline-none leading-4;
      }
    }
  }

  .main {
    @apply w-full;

    &.large .search-wrapper {
      input {
        @apply py-6;
      }
      svg.search-icon {
        margin: 7px 8px 8px 8px;
        @apply top-3.5;
      }
      .clear-icon {
        @apply top-2.5 m-2;
      }
    }

    &.medium {
      .search-wrapper input {
        @apply py-5;
      }
      svg.search-icon {
        @apply top-2.5 m-2;
      }
      .clear-icon {
        @apply top-1.5 m-2;
      }
    }
  }

  .footer {
    @apply flex items-center border-0 border-l border-solid border-gray-200 px-6 flex-shrink-0;
  }

  .filters {
    @apply shadow-lg rounded fixed z-30 bg-white flex flex-col overflow-y-auto;
    min-width: 360px;
    top: 57px;
  }

  .modal-overlay {
    @apply fixed inset-0 z-20;
  }
}
</style>
